import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  ListItemButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon,
  AutoAwesomeOutlined as AutoAwesomeOutlinedIcon,
  FactCheckOutlined as FactCheckOutlinedIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  SchoolOutlined as SchoolOutlinedIcon,
  SecurityOutlined as SecurityOutlinedIcon,
  SettingsSuggest as SettingsSuggestIcon,
  SubjectOutlined as SubjectOutlinedIcon,
  TuneOutlined as TuneOutlinedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import GeneralSettings from './sub_school_settings/GeneralSettings';
import AcademicSettings from './sub_school_settings/AcademicSettings';
import PermissionsSettings from './sub_school_settings/PermissionsSettings';
import { getStoredRoles, hasAllowedRole } from '../../utils/authSession';
import PageContainer from '../common/PageContainer';
import axiosInstance from '../../api/axiosConfig';

const buildSectionGroups = (canViewPermissions) => {
  const coreSettings = [
    {
      id: 'general',
      group: 'Core Settings',
      type: 'internal',
      title: 'School Profile & Branding',
      description: 'School identity, website, logos, login mode, report styles, and location defaults.',
      badge: 'Core',
      status: 'Live editor',
      icon: <SchoolOutlinedIcon fontSize="small" />,
    },
    {
      id: 'academic',
      group: 'Core Settings',
      type: 'internal',
      title: 'Academic Rules & Communication',
      description: 'Academic year, school hours, breaks, working days, SMS, WhatsApp, and absent alerts.',
      badge: 'Academic',
      status: 'Live editor',
      icon: <TuneOutlinedIcon fontSize="small" />,
    },
  ];

  if (canViewPermissions) {
    coreSettings.push({
      id: 'permissions',
      group: 'Core Settings',
      type: 'internal',
      title: 'Role Permissions',
      description: 'Manage access, module visibility, and system permissions for staff roles.',
      badge: 'Security',
      status: 'Restricted editor',
      icon: <SecurityOutlinedIcon fontSize="small" />,
    });
  }

  const linkedSystem = [
    {
      id: 'setup',
      group: 'Linked System Setup',
      type: 'linked',
      title: 'School Setup Wizard',
      description: 'Guided school onboarding for profile, classes, subjects, fees, and launch readiness.',
      badge: 'Setup',
      status: 'Launch guided flow',
      icon: <AutoAwesomeOutlinedIcon fontSize="small" />,
      path: '/ai-setup',
      actionLabel: 'Open School Setup',
      helperTitle: 'Guided setup for non-technical admins',
      helperDescription: 'Use the wizard when you want a simple step-by-step flow instead of editing each settings area manually.',
      quickLinks: [
        { label: 'Open guided setup', path: '/ai-setup' },
      ],
    },
    {
      id: 'academics-hub',
      group: 'Linked System Setup',
      type: 'linked',
      title: 'Classes, Subjects & Mapping',
      description: 'Manage classes, subject catalog, class-subject mapping, and teacher assignment from one area.',
      badge: 'Academics',
      status: 'Open linked modules',
      icon: <SubjectOutlinedIcon fontSize="small" />,
      path: '/classes',
      actionLabel: 'Open Academic Setup',
      helperTitle: 'Academic structure workspace',
      helperDescription: 'These linked modules define how the school is structured for attendance, exams, timetables, and teacher planning.',
      quickLinks: [
        { label: 'Manage classes', path: '/classes' },
        { label: 'Subjects', path: '/subjects' },
        { label: 'Class-subject mapping', path: '/class-subject-assign' },
        { label: 'Teacher-subject assign', path: '/subjects/teacher/assign' },
      ],
    },
    {
      id: 'fees-documents',
      group: 'Linked System Setup',
      type: 'linked',
      title: 'Fees, Documents & Certificates',
      description: 'Open fee setup, document settings, templates, and certificate generation controls.',
      badge: 'Operations',
      status: 'Open linked modules',
      icon: <ReceiptLongOutlinedIcon fontSize="small" />,
      path: '/fee-management',
      actionLabel: 'Open Operations Setup',
      helperTitle: 'Finance and document control',
      helperDescription: 'Use these linked pages to configure fee structures, document templates, and automated document generation.',
      quickLinks: [
        { label: 'Fee management', path: '/fee-management' },
        { label: 'Document settings', path: '/document-settings' },
        { label: 'Document templates', path: '/document-templates' },
        { label: 'Document automation', path: '/document-automation' },
      ],
    },
    {
      id: 'attendance-alerts',
      group: 'Linked System Setup',
      type: 'linked',
      title: 'Attendance & Absent Alerts',
      description: 'Open attendance operations and configure automatic absent notifications for SMS, WhatsApp, and in-app.',
      badge: 'Attendance',
      status: 'Linked to attendance',
      icon: <FactCheckOutlinedIcon fontSize="small" />,
      path: '/attendance?view=students',
      actionLabel: 'Open Attendance',
      helperTitle: 'Attendance trigger and alert operations',
      helperDescription: 'Student absentee alerts are configured in Academic Rules and triggered from attendance submission and manual absence updates.',
      quickLinks: [
        { label: 'Student attendance', path: '/attendance?view=students' },
        { label: 'Send notifications', path: '/send-notifications' },
      ],
    },
    {
      id: 'reports-exams',
      group: 'Linked System Setup',
      type: 'linked',
      title: 'Reports, Hall Tickets & Output',
      description: 'Quick access to reporting, hall tickets, and operational output modules.',
      badge: 'Output',
      status: 'Open linked modules',
      icon: <AssignmentTurnedInOutlinedIcon fontSize="small" />,
      path: '/reports',
      actionLabel: 'Open Output Workspace',
      helperTitle: 'Output and reporting workspace',
      helperDescription: 'Use these pages when the school needs printable or downloadable outputs for management, exams, and parents.',
      quickLinks: [
        { label: 'Reports', path: '/reports' },
        { label: 'Hall tickets', path: '/hall-tickets' },
      ],
    },
  ];

  return [...coreSettings, ...linkedSystem];
};

const SectionListItem = ({ item, active, onClick }) => (
  <ListItemButton
    onClick={onClick}
    selected={active}
    sx={{
      mb: 1,
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: active ? 'primary.main' : 'divider',
      alignItems: 'flex-start',
      py: 1.5,
      px: 1.5,
      bgcolor: active ? 'rgba(37, 99, 235, 0.08)' : 'background.paper',
      '&.Mui-selected': {
        bgcolor: 'rgba(37, 99, 235, 0.08)',
      },
      '&.Mui-selected:hover': {
        bgcolor: 'rgba(37, 99, 235, 0.12)',
      },
    }}
  >
    <Stack direction="row" spacing={1.2} sx={{ width: '100%' }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 2,
          display: 'grid',
          placeItems: 'center',
          color: active ? 'primary.main' : 'text.secondary',
          bgcolor: active ? 'rgba(37, 99, 235, 0.14)' : 'rgba(15, 23, 42, 0.05)',
          flexShrink: 0,
        }}
      >
        {item.icon}
      </Box>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {item.title}
          </Typography>
          <Chip
            label={item.badge}
            size="small"
            color={item.type === 'internal' ? 'primary' : 'default'}
            variant={item.type === 'internal' ? 'filled' : 'outlined'}
            sx={{ height: 22 }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.45 }}>
          {item.description}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.9 }}>
          {item.status}
        </Typography>
      </Box>
    </Stack>
  </ListItemButton>
);

const LinkedWorkspacePanel = ({ section, onNavigate }) => {
  const theme = useTheme();

  return (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 1.5,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
            theme.palette.info.main,
            0.06
          )} 100%)`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {section.helperTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6, maxWidth: 780 }}>
              {section.helperDescription}
            </Typography>
          </Box>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => onNavigate(section.path)}
          >
            {section.actionLabel}
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
        <Box sx={{ px: 2.25, py: 1.6, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'rgba(15, 23, 42, 0.02)' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Linked modules
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Open the exact school-system workspace you want from this settings directory.
          </Typography>
        </Box>
        <List disablePadding sx={{ p: 1.5 }}>
          {section.quickLinks.map((link) => (
            <Box
              key={link.path}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                px: 1.5,
                py: 1.35,
                mb: 1.2,
                '&:last-child': { mb: 0 },
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {link.label}
                </Typography>
                <Button size="small" variant="outlined" endIcon={<ArrowForwardIcon />} onClick={() => onNavigate(link.path)}>
                  Open
                </Button>
              </Stack>
            </Box>
          ))}
        </List>
      </Paper>
    </Stack>
  );
};

const SchoolSettings = () => {
  const navigate = useNavigate();
  const canViewPermissions = useMemo(
    () => hasAllowedRole(['SUPER_ADMIN', 'SCHOOL_ADMIN'], getStoredRoles()),
    []
  );

  const sectionItems = useMemo(() => buildSectionGroups(canViewPermissions), [canViewPermissions]);
  const groupedSections = useMemo(() => {
    return sectionItems.reduce((acc, item) => {
      if (!acc[item.group]) acc[item.group] = [];
      acc[item.group].push(item);
      return acc;
    }, {});
  }, [sectionItems]);

  const [activeSectionId, setActiveSectionId] = useState('general');
  const [exportingData, setExportingData] = useState(false);
  const [snackbarState, setSnackbarState] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!sectionItems.some((item) => item.id === activeSectionId)) {
      setActiveSectionId(sectionItems[0]?.id || 'general');
    }
  }, [activeSectionId, sectionItems]);

  const activeSection = sectionItems.find((item) => item.id === activeSectionId) || sectionItems[0];

  const handleCloseSnackbar = () => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  const handleSchoolExport = async () => {
    try {
      setExportingData(true);
      const response = await axiosInstance.get('/api/schools/export/current', {
        responseType: 'blob',
        timeout: 120000,
        skipGlobalError: true,
      });

      const contentDisposition = response.headers?.['content-disposition'] || '';
      const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const fileName = decodeURIComponent((fileNameMatch?.[1] || 'school-data-export.zip').replace(/"/g, '').trim());
      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbarState({
        open: true,
        message: 'School export is ready. The ZIP download has started.',
        severity: 'success',
      });
    } catch (error) {
      const message = error?.normalizedError?.message || error?.message || 'Failed to export school data.';
      setSnackbarState({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setExportingData(false);
    }
  };

  const renderWorkspace = () => {
    if (!activeSection) return null;
    if (activeSection.type === 'linked') {
      return <LinkedWorkspacePanel section={activeSection} onNavigate={navigate} />;
    }

    if (activeSection.id === 'general') {
      return <GeneralSettings />;
    }
    if (activeSection.id === 'academic') {
      return <AcademicSettings />;
    }
    if (activeSection.id === 'permissions' && canViewPermissions) {
      return <PermissionsSettings />;
    }
    return null;
  };

  return (
    <PageContainer sx={{ width: '100%' }}>
      <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              School Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>
              Manage school profile, academic rules, permissions, and linked setup pages.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<SettingsSuggestIcon />}
              onClick={() => navigate('/ai-setup')}
            >
              Open School Setup
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadOutlinedIcon />}
              onClick={handleSchoolExport}
              disabled={exportingData}
            >
              {exportingData ? 'Preparing Export...' : 'Export School Data'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid item xs={12} xl={3.2}>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, position: { xl: 'sticky' }, top: { xl: 88 } }}>
            <Typography variant="subtitle1" sx={{ px: 1, pb: 1.25, fontWeight: 800 }}>
              Settings
            </Typography>

            {Object.entries(groupedSections).map(([groupName, items], groupIndex) => (
              <Box key={groupName} sx={{ mb: groupIndex === Object.keys(groupedSections).length - 1 ? 0 : 1.25 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: 'block', px: 1, pb: 1, fontWeight: 700, letterSpacing: '0.08em' }}
                >
                  {groupName}
                </Typography>
                <List disablePadding>
                  {items.map((item) => (
                    <SectionListItem
                      key={item.id}
                      item={item}
                      active={item.id === activeSectionId}
                      onClick={() => setActiveSectionId(item.id)}
                    />
                  ))}
                </List>
                {groupIndex !== Object.keys(groupedSections).length - 1 ? <Divider sx={{ mt: 1 }} /> : null}
              </Box>
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} xl={8.8}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <Chip size="small" label={activeSection?.badge || 'Settings'} color={activeSection?.type === 'internal' ? 'primary' : 'default'} />
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {activeSection?.title}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65 }}>
                    {activeSection?.description}
                  </Typography>
                </Box>
                {activeSection?.type === 'linked' && activeSection?.path ? (
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate(activeSection.path)}
                  >
                    {activeSection.actionLabel}
                  </Button>
                ) : null}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 1.15, md: 1.5 }, borderRadius: 3 }}>
              {renderWorkspace()}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    School Exit Export
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Download one ZIP with school-linked data. Passwords, tokens, and secret keys are excluded.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={handleSchoolExport}
                  disabled={exportingData}
                >
                  {exportingData ? 'Preparing Export...' : 'Download ZIP'}
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={4500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarState.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default SchoolSettings;
